import DrawerNavigator, { type DrawerItem } from './drawer-navigator.tsx'
import { AppBar } from './app-bar.tsx'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import {
  type CompanyType,
  CompanyTypeEnum,
  RoleEnum,
  TransportTypeEnum,
  TransportTypeEnumSpanish,
} from 'types/enums.ts'
import { Icon } from '../ui/icons/icon.tsx'
import Footer from './Footer.tsx'
import { MainContainer } from './main-container.tsx'
import { adminDrawerItems } from './AdminLayout.tsx'
import { BadgeNumber } from '../ui/BadgeNumber.tsx'
import { type ModuleAccess } from '~/features/accessControl/modules.ts'

export const userDrawerItems = (
  alertsNumber: number,
  access: AccessProps,
  unseenRequests?: {
    land: number
    sea: number
    air: number
  },
): DrawerItem[] => [
  {
    title: 'Inicio',
    icon: <Icon size="drawer" className="-ml-[4px]" name="home" />,
    href: '/',
    isExactPath: true,
  },
  ...(access.iaValidationAccess
    ? [
        {
          title: 'Validación',
          icon: <Icon size="drawer" className="-ml-[4px]" name="badge-check" />,
          href: '/ai-validations',
          hrefMatch: 'ai-validation',
        },
        {
          title: 'Simulación',
          icon: <Icon size="drawer" className="-ml-[4px]" name="simulate" />,
          href: '/ai-simulations',
          hrefMatch: 'ai-simulation',
        },
      ]
    : []),
  ...(access.benchmarkAccess
    ? ([
        {
          title: 'Benchmark',
          icon: (
            <Icon size="drawer" className="-ml-[4px]" name="benchmark-small" />
          ),
          href: '/benchmark',
          children: [
            {
              title: 'Análisis',
              href: '/benchmark/analysis',
              isExactPath: true,
            },
            {
              title: 'Coste',
              href: '/benchmark/cost',
              isExactPath: true,
            },
            {
              title: 'Leadtime',
              icon: { name: 'lock', className: 'scale-[1.4] text-secondary' },
            },
            {
              title: 'Sostenibilidad',
              icon: { name: 'lock', className: 'scale-[1.4] text-secondary' },
            },
          ],
        },
      ] as DrawerItem[])
    : []),
  ...(access.smartConnectAccess
    ? ([
        {
          title: (
            <div className="flex items-center gap-2">
              Smart Connect
              <BadgeNumber number={alertsNumber} />
            </div>
          ),
          icon: <Icon size="drawer" className="-ml-[4px]" name="globe" />,
          href: '/smart-connect',
          isExactPath: true,
        },
      ] as DrawerItem[])
    : []),
  ...(access.tenderAccess
    ? [
        {
          title: 'Tender',
          icon: (
            <Icon size="drawer" className="-ml-[4px]" name="suppliers-small" />
          ),
          href: '/contact',
          isExactPath: true,
        },
      ]
    : []),
  ...(access.poolingAccess
    ? ([
        {
          title: 'Pooling',
          icon: <Icon size="drawer" className="-ml-[4px]" name="truck" />,
          href: `/transport-requests/${TransportTypeEnum.LAND}`,
          isExactPath: true,
          children: [
            {
              title: (
                <div className="flex items-center gap-2">
                  {TransportTypeEnumSpanish.land}
                  <BadgeNumber number={unseenRequests?.land ?? 0} />
                </div>
              ),
              icon: { name: 'road-bus' },
              href: `/transport-requests/${TransportTypeEnum.LAND}`,
            },
            {
              title: (
                <div className="flex items-center gap-2">
                  {TransportTypeEnumSpanish.sea}
                  <BadgeNumber number={unseenRequests?.sea ?? 0} />
                </div>
              ),
              icon: { name: 'ship' },
              href: `/transport-requests/${TransportTypeEnum.SEA}`,
            },
            {
              title: (
                <div className="flex items-center gap-2">
                  {TransportTypeEnumSpanish.air}
                  <BadgeNumber number={unseenRequests?.air ?? 0} />
                </div>
              ),
              icon: { name: 'plane' },
              href: `/transport-requests/${TransportTypeEnum.AIR}`,
            },
          ],
        },
      ] as DrawerItem[])
    : []),
  ...(access.controlDocumentAccess
    ? ([
        {
          title: 'Documentos de control',
          icon: <Icon size="drawer" className="-ml-[4px]" name="file" />,
          href: '/control-documents',
          hrefMatch: 'control-documents',
        },
      ] as DrawerItem[])
    : []),
  ...(access.iaValidationAccess || access.benchmarkAccess
    ? ([
        {
          title: 'Subir Factura',
          icon: (
            <Icon
              size="drawer"
              className="-ml-[4px]"
              name="upload-files-small"
            />
          ),
          linkClassName:
            'bg-secondary-500 !text-foreground font-bold hover:bg-secondary',
          href: '/upload',
          isExactPath: true,
        },
      ] as DrawerItem[])
    : []),
]

export const carrierUserDrawerItems = (
  unseenAcceptedOffers?: number,
): DrawerItem[] => [
  {
    title: 'Envíos',
    icon: <Icon size="drawer" className="-ml-[4px]" name="suppliers-small" />,
    href: `/carriers/shipments/${TransportTypeEnum.LAND}`,
    isExactPath: true,
    children: [
      {
        title: TransportTypeEnumSpanish.land,
        icon: { name: 'road-bus' },
        href: `/carriers/shipments/${TransportTypeEnum.LAND}`,
      },
      {
        title: TransportTypeEnumSpanish.sea,
        icon: { name: 'ship' },
        href: `/carriers/shipments/${TransportTypeEnum.SEA}`,
      },
      {
        title: TransportTypeEnumSpanish.air,
        icon: { name: 'plane' },
        href: `/carriers/shipments/${TransportTypeEnum.AIR}`,
      },
    ],
  },
  {
    title: (
      <div className="flex items-center gap-2">
        Calendario
        <BadgeNumber number={unseenAcceptedOffers ?? 0} />
      </div>
    ),
    icon: <Icon size="drawer" className="-ml-[4px]" name="calendar" />,
    href: '/carriers/calendar',
    isExactPath: true,
  },
]

type Props = {
  children: React.ReactNode
  userName?: string
  userCompanyType?: CompanyType
  toasts?: Array<{
    title: string
    description?: string
    variant?: 'default' | 'success' | 'error' | null
  }> // toasts with the success or error of uploading invoices, rates or validations
} & RoleProps

type AccessProps = ModuleAccess

type RoleProps =
  | {
      userRole: typeof RoleEnum.USER
      alertsNumber: number
      unseenRequests?: {
        land: number
        sea: number
        air: number
      }
      unseenAcceptedOffers?: number
      access: AccessProps
    }
  | {
      userRole?: typeof RoleEnum.ADMIN
    }

export const MainLayout = ({
  children,
  userRole,
  userName = '',
  userCompanyType,
  ...otherProps
}: Props) => {
  const isMobile = useMediaQuery({
    query: '(max-width: 768px)',
  })
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean | undefined>(
    undefined,
  )

  useEffect(() => {
    if (isDrawerOpen === undefined) {
      setIsDrawerOpen(!isMobile)
    }
  }, [isMobile, isDrawerOpen])

  const drawerItems =
    'alertsNumber' in otherProps
      ? userRole === RoleEnum.USER &&
        userCompanyType === CompanyTypeEnum.SHIPPER
        ? userDrawerItems(
            otherProps.alertsNumber,
            otherProps.access,
            otherProps.unseenRequests,
          )
        : carrierUserDrawerItems(otherProps.unseenAcceptedOffers)
      : adminDrawerItems

  return (
    <MainContainer>
      <AppBar
        userName={userName}
        userRole={userRole}
        onHamburgerMenu={() => setIsDrawerOpen(!isDrawerOpen)}
        userTo={
          userRole === RoleEnum.USER &&
          userCompanyType === CompanyTypeEnum.SHIPPER
            ? '/employees'
            : '/carriers/user'
        }
      />
      <div className="mx-auto flex min-h-full w-full sm:mt-[70px]">
        <DrawerNavigator
          isMobile={true}
          items={drawerItems}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
        <DrawerNavigator
          isMobile={false}
          items={drawerItems}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
        <div className="w-full overflow-x-auto sm:ml-8 lg:ml-16">
          <div className="mt-20 min-h-[calc(100vh-122px)] sm:mt-0">
            {children}
          </div>
          <Footer variant="desktop" />
        </div>
      </div>
      <Footer variant="mobile" />
    </MainContainer>
  )
}
